import React, { useState, useEffect } from 'react';
import { api } from '../App';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const COLORS = ['#0066cc', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#20c997'];

function StatCard({ title, value, subtitle, color, trend }) {
  return (
    <div className="card" style={{ textAlign: 'center' }}>
      <h3 style={{ fontSize: 13, color: '#666', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</h3>
      <div style={{ fontSize: 32, fontWeight: 700, color: color || '#1a1a2e' }}>{value}</div>
      {subtitle && <p style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{subtitle}</p>}
      {trend && (
        <p style={{ fontSize: 12, color: trend > 0 ? '#28a745' : '#dc3545', marginTop: 4 }}>
          {trend > 0 ? '↑' : '↓'} {Math.abs(trend)} this week
        </p>
      )}
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api('/admin/stats'),
      api('/admin/analytics')
    ])
      .then(([statsData, analyticsData]) => {
        setStats(statsData);
        setAnalytics(analyticsData);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading dashboard...</div>;
  if (!stats || !analytics) return <div style={{ padding: 40, textAlign: 'center', color: '#dc3545' }}>Failed to load data</div>;

  // Format dates for charts
  const userGrowthData = analytics.userGrowth.map(d => ({
    ...d,
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }));

  const requestTrendsData = analytics.requestTrends.map(d => ({
    ...d,
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }));

  // Format ratings for chart
  const ratingsData = [1, 2, 3, 4, 5].map(rating => {
    const found = analytics.ratingsDistribution.find(r => r.rating === rating);
    return { rating: `${rating} Star`, count: found ? found.count : 0 };
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>Dashboard</h1>
        <div style={{ fontSize: 13, color: '#666' }}>
          Last 7 days: {analytics.weeklyStats.new_users} new users, {analytics.weeklyStats.new_requests} requests
        </div>
      </div>
      
      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard title="Total Users" value={stats.users.total} trend={analytics.weeklyStats.new_users} />
        <StatCard title="Yacht Owners" value={stats.users.owners} color="#0066cc" />
        <StatCard title="Helpers" value={stats.users.helpers} color="#28a745" />
        <StatCard title="Active Requests" value={stats.requests.active} color="#ffc107" />
        <StatCard title="Avg Rating" value={stats.ratings.average} color="#f59e0b" subtitle={`${stats.ratings.total} reviews`} />
      </div>

      {/* Charts Row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* User Growth Chart */}
        <div className="card">
          <h3 style={{ marginBottom: 20 }}>User Growth (Last 30 Days)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={userGrowthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="owners" stackId="1" stroke="#0066cc" fill="#0066cc" fillOpacity={0.6} name="Owners" />
              <Area type="monotone" dataKey="helpers" stackId="1" stroke="#28a745" fill="#28a745" fillOpacity={0.6} name="Helpers" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* User Type Pie Chart */}
        <div className="card">
          <h3 style={{ marginBottom: 20 }}>User Breakdown</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={analytics.userTypes}
                dataKey="count"
                nameKey="user_type"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ user_type, count }) => `${user_type}: ${count}`}
              >
                {analytics.userTypes.map((entry, index) => (
                  <Cell key={entry.user_type} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Request Trends */}
        <div className="card">
          <h3 style={{ marginBottom: 20 }}>Request Activity (Last 30 Days)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={requestTrendsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" stroke="#0066cc" strokeWidth={2} name="New Requests" dot={{ r: 3 }} />
              <Line type="monotone" dataKey="completed" stroke="#28a745" strokeWidth={2} name="Completed" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Ratings Distribution */}
        <div className="card">
          <h3 style={{ marginBottom: 20 }}>Ratings Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={ratingsData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="rating" tick={{ fontSize: 11 }} width={60} />
              <Tooltip />
              <Bar dataKey="count" fill="#f59e0b" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 3 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Request Status */}
        <div className="card">
          <h3 style={{ marginBottom: 20 }}>Requests by Status</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={analytics.requestsByStatus}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="status" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {analytics.requestsByStatus.map((entry, index) => (
                  <Cell key={entry.status} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top Ports */}
        <div className="card">
          <h3 style={{ marginBottom: 20 }}>Top Ports by Requests</h3>
          {analytics.topPorts.length > 0 ? (
            <div>
              {analytics.topPorts.map((port, idx) => (
                <div key={port.name} style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: idx < analytics.topPorts.length - 1 ? '1px solid #eee' : 'none'
                }}>
                  <div style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: COLORS[idx],
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    marginRight: 12
                  }}>
                    {idx + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{port.name}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>{port.country}</div>
                  </div>
                  <div style={{ fontWeight: 600, color: COLORS[idx] }}>{port.requests}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No port data</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
